import type { ApolloClient } from '@apollo/client';
import { getHouseholdModelPortfolioSecurities } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import {
  FETCH_HOUSEHOLD_ATTACHED_MODEL,
  FETCH_HOUSEHOLD_MODEL_PORTFOLIO_DETAILS,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/queries';
import { cloneDeep } from 'lodash-es';
import type { EditedHouseholdModelPortfolio } from './householdAllocations';

type FetchHouseholdAttachedModelData = {
  fetchClientGroup?: {
    clientGroup?: {
      organization?: { id?: string } | null;
      financialProduct?: { id?: string } | null;
    } | null;
  } | null;
};

type FetchHouseholdModelPortfolioDetailsData = {
  fetchModelPortfolio?: {
    modelPortfolio?: HouseholdModelPortfolioModel | null;
  } | null;
};

export type LoadHouseholdAttachedModelResult = {
  model: HouseholdModelPortfolioModel | undefined;
  householdOrganizationId: string | null;
};

/**
 * The same value with Apollo's stamps taken off.
 *
 * A model portfolio arrives as some sixty objects once its holdings and their
 * asset classes are counted, each carrying a `__typename` nothing here reads.
 * The workflow context is written to the server whole on every change — and a
 * household workflow holds the model three times over, as the draft, the rows
 * the securities step works in, and the baseline — so the stamps are paid for
 * again on every weight the advisor types.
 */
const withoutTypenames = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => withoutTypenames(item)) as unknown as T;
  }
  if (value === null || typeof value !== 'object') return value;

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([key]) => key !== '__typename')
      .map(([key, item]) => [key, withoutTypenames(item)]),
  ) as T;
};

/**
 * The model as the workflow's own copy to edit.
 *
 * Carries the holdings twice on purpose: as `children`, which is how a model
 * portfolio is read and how a proposal is built, and as the table rows the
 * securities step works in. That step is shared with the shelf model flow and
 * knows only about rows, so a draft staged without them opens it on an empty
 * table offering to import a CSV, for a model that already holds everything it
 * needs.
 */
export const stagedHouseholdDraft = (
  model: HouseholdModelPortfolioModel,
): EditedHouseholdModelPortfolio => ({
  ...withoutTypenames(cloneDeep(model)),
  securities: withoutTypenames(getHouseholdModelPortfolioSecurities(model)),
});

/**
 * The record of what the household held before this workflow touched it.
 *
 * Kept out of reach of the edits, and never restaged: on the Change branch the
 * household is repointed at a new model part way through, so a second look at
 * what is attached would answer with the proposal rather than what it replaces.
 */
export const stagedHouseholdBaseline = (
  model: HouseholdModelPortfolioModel,
): HouseholdModelPortfolioModel => withoutTypenames(cloneDeep(model));

/** What the household record itself says: its owning org and what it holds. */
const loadHouseholdAttachment = async (
  graphqlClient: ApolloClient<object>,
  householdId: string,
) => {
  const { data } = await graphqlClient.query<FetchHouseholdAttachedModelData>({
    query: FETCH_HOUSEHOLD_ATTACHED_MODEL,
    variables: { clientGroupId: householdId },
    fetchPolicy: 'network-only',
  });

  return {
    attachedModelId: data?.fetchClientGroup?.clientGroup?.financialProduct?.id,
    householdOrganizationId:
      data?.fetchClientGroup?.clientGroup?.organization?.id ?? null,
  };
};

/**
 * The organization that owns the household, for steps that need nothing else.
 *
 * Kept apart from the attached model so a branch that only has to scope a
 * mutation does not also fetch the sixty-odd objects a model portfolio is.
 */
export const loadHouseholdOrganizationId = async (
  graphqlClient: ApolloClient<object>,
  householdId: string,
): Promise<string | null> =>
  (await loadHouseholdAttachment(graphqlClient, householdId))
    .householdOrganizationId;

/**
 * The model the household has attached, in full, beside the org that owns it.
 *
 * An undefined model covers both nothing attached and a model the org cannot
 * see, which a step can do no more about than the other.
 */
export const loadHouseholdAttachedModel = async (
  graphqlClient: ApolloClient<object>,
  householdId: string,
): Promise<LoadHouseholdAttachedModelResult> => {
  const { attachedModelId, householdOrganizationId } =
    await loadHouseholdAttachment(graphqlClient, householdId);

  if (!attachedModelId) return { model: undefined, householdOrganizationId };

  const { data } =
    await graphqlClient.query<FetchHouseholdModelPortfolioDetailsData>({
      query: FETCH_HOUSEHOLD_MODEL_PORTFOLIO_DETAILS,
      variables: { input: { modelPortfolioId: attachedModelId } },
      fetchPolicy: 'network-only',
    });

  return {
    model: data?.fetchModelPortfolio?.modelPortfolio ?? undefined,
    householdOrganizationId,
  };
};
